/**
 * Phase 17 Plan 17-02 — KpiTemplatePickerDialog
 *
 * Lists KPI templates from useKpiTemplatesQuery.
 * Clicking "Apply" on a row calls props.onSelect(template) — the parent
 * (WorkOrderDetailPage) uses this to open CreateKpiDialog with initialValues
 * pre-populated (copy semantics, no live link).
 */

import type { JSX } from 'react';

import { formatKpiUnit } from '@/components/kpis/KpiUnitDisplay';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useKpiTemplatesQuery } from '@/hooks/useKpiTemplateQueries';
import type { KpiTemplate } from '@/types/kpi-template';

export interface KpiTemplatePickerDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (v: boolean) => void;
  readonly onSelect: (template: KpiTemplate) => void;
}

export function KpiTemplatePickerDialog({
  open,
  onOpenChange,
  onSelect,
}: KpiTemplatePickerDialogProps): JSX.Element {
  const templatesQuery = useKpiTemplatesQuery();
  const templates = templatesQuery.data ?? [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="kpi-template-picker-dialog" className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Use KPI Template</DialogTitle>
          <DialogDescription>
            Select a template to pre-populate the KPI form. Fields can be edited before saving.
          </DialogDescription>
        </DialogHeader>

        {templatesQuery.isLoading && (
          <p className="text-sm text-muted-foreground py-4" data-testid="kpi-templates-loading">
            Loading templates…
          </p>
        )}

        {!templatesQuery.isLoading && templatesQuery.isError && (
          <div className="py-8 text-center" data-testid="kpi-templates-error">
            <p className="text-sm text-destructive">
              {templatesQuery.error.status === 403
                ? 'You don’t have permission to view KPI templates.'
                : 'Failed to load KPI templates.'}
            </p>
            {templatesQuery.error.status !== 403 && templatesQuery.error.detail !== undefined && (
              <p className="text-muted-foreground text-xs mt-1">
                {templatesQuery.error.detail}
              </p>
            )}
          </div>
        )}

        {!templatesQuery.isLoading && !templatesQuery.isError && templates.length === 0 && (
          <div className="py-8 text-center" data-testid="kpi-templates-empty">
            <p className="text-muted-foreground text-sm">No KPI templates available.</p>
            <p className="text-muted-foreground text-xs mt-1">
              An admin can create templates in the KPI Templates page.
            </p>
          </div>
        )}

        {!templatesQuery.isError && templates.length > 0 && (
          <Table data-testid="kpi-templates-table">
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Unit</TableHead>
                <TableHead>Description</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {templates.map((t) => (
                <TableRow key={t.id} data-testid={`kpi-template-row-${String(t.id)}`}>
                  <TableCell className="font-medium">{t.name}</TableCell>
                  <TableCell>{formatKpiUnit(t.unit, t.customUnitText)}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {t.description ?? '—'}
                  </TableCell>
                  <TableCell>
                    <Button
                      type="button"
                      size="sm"
                      onClick={() => {
                        onSelect(t);
                        onOpenChange(false);
                      }}
                      data-testid={`kpi-template-apply-${String(t.id)}`}
                    >
                      Apply
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => { onOpenChange(false); }}
            data-testid="kpi-template-picker-close"
          >
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
